import React, { useEffect, useState } from 'react';
import { Dropdown, MultiSelectDropdown, useToasts } from 'freemium-ui';
import { get, isNil } from 'lodash';
import moment from 'moment-timezone';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import XLSX from 'xlsx';
import UploadFile from 'routes/components/UploadFile';
import { CONTACTS_IMPORT_LIMIT } from 'routes/constants';
import { getAccountTimeFormat } from 'shared/utils';
import {
  AccountInfoType,
  ContactListType,
  FormServFieldType,
} from '../../../../../shared/types';
import { CustomFileType, ImportContactsFromType } from '../types';
import Icon from '../../../../../shared/components/Icon';
import {
  HIDE_IMPORT_CONTACT_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
  UNSUPPORTED_FIELDS_FOR_NOW,
} from '../../../../../shared/constants';
import { ErrorField, FormField, FreshLink } from '../../../../../shared/styles';
import { useSaveContactList } from '../../../../screens/lists/hooks';
import { getAllFileColumnNamesFromJson, getAutoMapping } from '../utils';
import {
  ApiImport,
  CustomSelect,
  ImportContactsContainer,
  ImportDisplayBox, // IntegrateBox,
  MappingTable,
} from './styles';
import { sampleData } from './constants';

type ContactsImporterProps = {
  contactLists: ContactListType[];
  label?: string;
  component: 'import' | 'new-list';
  isLoadingContactLists?: boolean;
  fields: FormServFieldType[];
  hideListAdd?: boolean;
  form: UseFormReturn<ImportContactsFromType>;
  accountInfo?: AccountInfoType;
  contactsCount: number;
};

const downloadSampleData = () => {
  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(sampleData);
  const wb: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws);
  XLSX.writeFile(wb, 'sample.csv');
};

const ContactsImporter = (props: ContactsImporterProps) => {
  const { t } = useTranslation();
  const {
    label,
    fields,
    form,
    component,
    contactLists,
    isLoadingContactLists,
    hideListAdd,
    accountInfo,
    contactsCount,
  } = props;
  const [, setToast] = useToasts();
  const [clearFile, setClearFile] = useState(false);

  useEffect(() => {
    if (clearFile) {
      setTimeout(() => setClearFile(false), 200);
    }
  }, [clearFile]);

  const [createdList, setCreatedList] = React.useState<ContactListType>();
  const allContactLists = React.useMemo<Record<string, ContactListType>>(() => {
    const newContactLists = {};
    contactLists.forEach((contactList: ContactListType) => {
      newContactLists[contactList.display_id || ''] = contactList;
    });
    return newContactLists;
  }, [contactLists]);
  const contactFields = fields?.filter(
    (field) =>
      field.editable &&
      !UNSUPPORTED_FIELDS_FOR_NOW.includes(field.type) &&
      ![...HIDE_IMPORT_CONTACT_FIELDS, ...TEMPORARY_HIDDEN_FIELDS].includes(
        field.name,
      ),
  );

  const contactsLimit = React.useMemo(() => {
    if (isNil(accountInfo?.resource_limit_configuration?.contacts))
      return Math.max(CONTACTS_IMPORT_LIMIT - contactsCount, 0);
    const limit = Number(accountInfo?.resource_limit_configuration?.contacts);
    if (isNaN(limit) || limit === 0)
      return Math.max(CONTACTS_IMPORT_LIMIT - contactsCount, 0);
    return Math.max(limit - contactsCount, 0);
  }, [accountInfo?.resource_limit_configuration?.contacts, contactsCount]);

  const {
    register,
    watch,
    setValue,
    formState: { submitCount, errors },
  } = form;

  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleFileUpload = async (event) => {
    const file: File = event.target.files && event.target.files[0];
    if (file.size > 5000000) {
      setToast({
        type: 'error',
        delay: 3000,
        text: `${t('forms.toast.error.size_exceeded')}`,
      });
      setClearFile(true);
      return;
    }
    if (!file) {
      setToast({
        type: 'error',
        delay: 3000,
        text: `${t('forms.toast.error.invalid_file')}`,
      });
      setClearFile(true);
      return;
    }
    const bufferData = await file?.arrayBuffer();
    const workbook = XLSX.read(bufferData, { raw: true });
    const worksheetname = workbook.SheetNames[0];
    const ws = workbook.Sheets[worksheetname];
    const jsondata: any[] = XLSX.utils.sheet_to_json(ws, {
      raw: true,
      defval: null,
    });
    if (jsondata.length > contactsLimit) {
      setToast({
        type: 'error',
        delay: 3000,
        text: `${t('forms.toast.error.max_records', {
          limit: contactsLimit,
        })}`,
      });
      setClearFile(true);
      return;
    }
    setFormValue('fileMetaData', {
      lastModified: new Date(file.lastModified).toISOString(),
      name: file.name,
      size: file.size,
      data: jsondata,
      type: file.type,
    });
  };

  React.useEffect(() => {
    register('addToLists');
    register('importContacts');
    register('fileMetaData');
  }, []);

  const addToLists = watch('addToLists') || [];
  const importContacts = watch('importContacts');
  const fileMetaData: CustomFileType = watch('fileMetaData') || {};
  const showError = submitCount > 0;

  const allFileColumns = getAllFileColumnNamesFromJson(
    fileMetaData?.data || [],
  );

  const items = allFileColumns.filter(
    (fileColumn) => !Object.values(importContacts || {}).includes(fileColumn),
  );

  const { mutate: saveContactList, isLoading: isSavingContactList } =
    useSaveContactList({
      setToast,
      callback: (isSuccess: boolean, data: ContactListType) => {
        if (isSuccess && data.display_id) {
          allContactLists[data.display_id] = data;
          setCreatedList(data);
        }
      },
    });

  React.useEffect(() => {
    if (createdList && createdList.display_id) {
      setFormValue('addToLists', [
        ...(addToLists || []),
        createdList.display_id,
      ]);
      setCreatedList({});
    }
  }, [contactLists]);

  React.useEffect(() => {
    if (!fileMetaData.name) return;
    const autoMapping = getAutoMapping({
      fields: contactFields,
      fileColumns: allFileColumns,
    });
    setFormValue('importContacts', autoMapping);
  }, [fileMetaData]);

  return fileMetaData.name ? (
    <ImportContactsContainer>
      <UploadFile
        file={fileMetaData}
        onRemoveFile={() => {
          setFormValue('addToLists', undefined);
          setFormValue('fileMetaData', undefined);
          setFormValue('importContacts', {});
        }}
      />
      {!hideListAdd && (
        <FormField>
          <MultiSelectDropdown
            {...(isSavingContactList || isLoadingContactLists
              ? { isOpen: false }
              : {})}
            items={contactLists}
            placeholder={
              isSavingContactList
                ? `${t('forms.placeholders.adding_contact')}`
                : `${t('forms.placeholders.search_and_create_list')}`
            }
            id="modal-dd-addToLists"
            customValues
            name="addToLists"
            label={`${t('forms.labels.add_contacts_to_list')}`}
            selectedItem={addToLists.map((id: string) => allContactLists[id])}
            maxAllowedLength={128}
            disabled={isSavingContactList}
            onCustomValueAdd={(val) => {
              if (val?.display_id) return;
              saveContactList({
                payload: {
                  description: `Contacts Import ${moment()
                    .tz(accountInfo?.timezone || moment.tz.guess())
                    .format(getAccountTimeFormat(accountInfo))}`,
                  label:
                    val?.label ||
                    `Contacts Import ${moment()
                      .tz(accountInfo?.timezone || moment.tz.guess())
                      .format(getAccountTimeFormat(accountInfo))}`,
                },
              });
            }}
            onChange={(val: ContactListType[]) => {
              setFormValue(
                'addToLists',
                val.map((v: ContactListType) => v.display_id),
              );
            }}
            isErrored={
              showError && get(errors, ['addToLists', 'message'], false)
            }
            errorText={
              showError ? get(errors, ['addToLists', 'message'], '') : ''
            }
            itemLabelIdentifier="label"
            itemValueIdentifier="display_id"
          />
        </FormField>
      )}

      <div className="info">
        {t('contacts.import_contact.map_contact')}
        {items.length > 0 && (
          <>
            {items.length > 1 ? (
              <Trans
                i18nKey="contacts.import_contact.unmapped_columns"
                components={{
                  bold: <b />,
                }}
                values={{ length: items.length }}
              />
            ) : (
              <Trans
                i18nKey="contacts.import_contact.unmapped_column"
                components={{
                  bold: <b />,
                }}
              />
            )}
          </>
        )}
      </div>
      {showError && get(errors, ['importContacts', 'message'], false) && (
        <ErrorField>
          {get(errors, ['importContacts', 'message'], '')}
        </ErrorField>
      )}
      <MappingTable>
        <thead>
          <tr>
            <th>{t('contacts.import_contact.contact_field')}</th>
            <th className="flex">
              <span>{t('contacts.file_column')}</span>
              <Icon fill="none" width="16" height="16" glyph="paper-clip" />
            </th>
            <th>{t('contacts.import_contact.mapped')}</th>
          </tr>
        </thead>
        <tbody>
          {contactFields.map((contactField, key) => {
            const selectedItem =
              allFileColumns.find(
                (item) => item === importContacts?.[contactField.name],
              ) || null;
            return (
              <tr key={key}>
                <td className="key">
                  <CustomSelect>
                    {contactField?.label}
                    {contactField?.required && <span className="red">*</span>}
                  </CustomSelect>
                </td>
                <td>
                  <Dropdown
                    id={`modal-dd-${contactField}`}
                    isErrored={
                      showError &&
                      get(
                        errors,
                        ['importContacts', contactField.name, 'message'],
                        false,
                      )
                    }
                    errorText={get(
                      errors,
                      ['importContacts', contactField.name, 'message'],
                      '',
                    )}
                    type="input"
                    itemComponent={(fileColumn: string) => fileColumn}
                    itemToString={(fileColumn: string) => fileColumn}
                    defaultSelectedItem={selectedItem || null}
                    placeholder={`${t('forms.placeholders.search_fields')}`}
                    items={items}
                    emptyItemsText={`${t('no_results_found')}`}
                    onChange={(fileColumn: string) => {
                      setFormValue('importContacts', {
                        ...(importContacts || {}),
                        [contactField.name]: fileColumn,
                      });
                    }}
                  />
                </td>
                <td className="center">
                  {importContacts?.[contactField.name] && (
                    <Icon
                      glyph="green-tick-contained"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </MappingTable>
    </ImportContactsContainer>
  ) : (
    <ImportContactsContainer>
      {label && <div className="label">{label}</div>}
      <div className="info-label">
        <Trans
          i18nKey="contacts.import_contact.import_contacts_using_file_or_csv"
          components={{
            aTag: (
              <FreshLink
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  downloadSampleData();
                }}
              />
            ),
          }}
        />
        {/* <Tooltip
          label="Click to learn how to import"
          style={{ height: '14px' }}
          position="top"
        >
          <Icon width="14" height="14" glyph="question" />
        </Tooltip> */}
      </div>
      <UploadFile
        onChange={handleFileUpload}
        clearInput={clearFile}
        accept=".csv, .xlsx, .xls"
      >
        <>
          <div className="lev1">{t('contacts.upload')}</div>
          <div className="lev2">{t('contacts.max_file_size')}</div>
          <div className="lev2">
            {t('contacts.import_contact.import_limit', {
              count: contactsLimit,
            })}
          </div>
          {/* <div className="lev3">Download Sample CSV</div> */}
          {fileMetaData.name && <div className="name">{fileMetaData.name}</div>}
        </>
      </UploadFile>
      <div className="condition" />
      <ImportDisplayBox>
        {component === 'import' && (
          <ApiImport>
            <span>
              <Trans
                i18nKey="contacts.import_contact.import_contact_via_api"
                components={{
                  aTag: (
                    <FreshLink
                      href="https://support.freshsurvey.io/en/support/solutions/articles/50000004724-api-documentation/"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </span>
          </ApiImport>
        )}
        {component === 'new-list' && (
          <ApiImport>
            <span>
              <Trans
                i18nKey="contacts.import_contact.create_list_via_api"
                components={{
                  aTag: (
                    <FreshLink
                      href="https://support.freshsurvey.io/en/support/solutions/articles/50000004724-api-documentation/"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </span>
          </ApiImport>
        )}
      </ImportDisplayBox>
      {/* <ImportDisplayBox>
        <IntegrateBox>
          <div className="lev1">Integrate and Active sync contacts with</div>
          <div className="lev2">Freshsuccess, Freshdesk, Freshworks CRM</div>
          <FreshLink
            href="https://support.freshsurvey.io/en/support/home"
            target="_blank"
            rel="noreferrer"
          >
            Integrate now
          </FreshLink>
        </IntegrateBox>
      </ImportDisplayBox> */}
    </ImportContactsContainer>
  );
};

export default ContactsImporter;
